(function () {
  // Init guard: the cart drawer can appear in more than one place, but
  // we only want one set of listeners / one fetch wrapper per page.
  if (window.CartUpsellInit) return;
  window.CartUpsellInit = true;

  // Marks our own cart calls so the fetch observer below ignores them
  // (prevents an infinite refresh loop). Protection writes must still
  // refresh shipping eligibility; the section read below is not a cart write.
  const OWN_MARKER = '_cartupsell=1';
  // The AOV bundle app's "leave this request alone" signal. Load-bearing
  // (see cart-drawer-shipping-protection.liquid for the full note).
  const BUNDLE_SKIP = 'app=aov-ai-bundle&skip=true';
  let revision = 0;

  const showOffer = (root, index = 0) => {
    const cards = [...root.querySelectorAll('[data-cart-upsell-card]')];
    if (!cards.length) return;
    index = (index + cards.length) % cards.length;
    cards.forEach((card, i) => { card.hidden = i !== index; });
    root.dataset.offerIndex = String(index);
    root.querySelector('[data-cart-upsell-carousel]').dataset.initialized = 'true';
    root.querySelectorAll('[data-cart-upsell-prev], [data-cart-upsell-next]').forEach(button => { button.hidden = cards.length < 2; });
    const status = root.querySelector('[data-cart-upsell-position]');
    status.hidden = cards.length < 2;
    const text = status.dataset.template.replaceAll('[current]', index + 1).replaceAll('[total]', cards.length);
    if (status.textContent !== text) status.textContent = text;
  };

  document.addEventListener('click', event => {
    const arrow = event.target.closest('[data-cart-upsell-prev], [data-cart-upsell-next]');
    if (!arrow) return;
    const root = arrow.closest('[data-cart-upsell-root]');
    showOffer(root, Number(root.dataset.offerIndex || 0) + (arrow.hasAttribute('data-cart-upsell-next') ? 1 : -1));
  });

  // The cart drawer panel uses transform + overflow:hidden, which traps
  // and clips any position:fixed descendant - so a modal rendered inside
  // the drawer can't overlay the viewport. Move our modal out to <body>
  // where it can. The modal-opener still finds it by id wherever it
  // lives. Called on init and after every refresh (a refresh re-creates
  // the in-card modal, so we de-dupe: keep the body copy, drop the new).
  const relocateModals = () => {
    const seen = new Set();
    document.querySelectorAll('quick-add-modal[id^="CartOfferModal-"]').forEach((modal) => {
      const onBody = seen.has(modal.id);
      seen.add(modal.id);
      if (onBody) {
        modal.remove();
      } else if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
    });
  };

  // Swap the parts of the cart drawer that depend on cart state from
  // freshly server-rendered HTML: the line items, the footer
  // (subtotal / savings - currency-correct, no JS formatting), and our
  // own upsell root (so the metafield logic re-evaluates: appear /
  // update / hide). Mirrors the protection snippet's approach.
  const applyDrawerSections = (sectionsHtml) => {
    if (!sectionsHtml) return;
    for (const html of Object.values(sectionsHtml)) {
      if (typeof html !== 'string' || !html) continue;
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      const newItems = parsed.querySelector('cart-drawer-items');
      const curItems = document.querySelector('cart-drawer-items');
      if (newItems && curItems) curItems.innerHTML = newItems.innerHTML;

      for (const selector of ['#CartDrawer .drawer__heading', '#CartDrawer .cart-drawer__count-bubble']) {
        const next = parsed.querySelector(selector);
        const current = document.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      }

      const newFooter = parsed.querySelector('.cart-drawer__footer');
      const curFooter = document.querySelector('.cart-drawer__footer');
      if (newFooter && curFooter) curFooter.innerHTML = newFooter.innerHTML;

      const newRoot = parsed.querySelector('[data-cart-upsell-root]');
      const curRoot = document.querySelector('[data-cart-upsell-root]');
      if (newRoot && curRoot) {
        const handle = curRoot.querySelector('[data-cart-upsell-card]:not([hidden])')?.dataset.upsellHandle;
        curRoot.innerHTML = newRoot.innerHTML;
        initialize(curRoot, handle);
      }
      const newShipping = parsed.querySelector('cart-shipping-confirmation');
      const curShipping = document.querySelector('#CartDrawer cart-shipping-confirmation');
      if (newShipping && curShipping) curShipping.replaceWith(newShipping);
      break;
    }
    relocateModals();
  };

  // All our cart writes go through here: append the bundle-skip + own
  // marker, ask for the cart-drawer section back, and apply it.
  const postCartChange = async (path, body) => {
    const requestRevision = ++revision;
    const sep = path.includes('?') ? '&' : '?';
    const url = `${path}${sep}${BUNDLE_SKIP}&${OWN_MARKER}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: location.pathname }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.description || data?.message || `Cart op failed: ${response.status}`);
    if (requestRevision === revision) applyDrawerSections(data.sections);
    else refreshCardOnly();
    // This theme's CartItems subscriber otherwise copies the full cart-page layout into the drawer.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: data });
    return data;
  };

  // ---- colour dot resolver ----
  // Try, in order: native swatch image, native swatch hex, a valid CSS
  // named colour from the option text, else hide the dot. CSS.supports
  // lets us validate "light blue" etc. without a hardcoded list.
  const cssColorIsValid = (name) => {
    if (!name) return false;
    try { return CSS.supports('color', name); } catch (_) { return false; }
  };

  const updateDot = (root, opt) => {
    const dot = root.querySelector('[data-cart-upsell-dot]');
    if (!dot || !opt) return;
    const img = opt.dataset.swatchImage;
    const hex = opt.dataset.swatchColor;
    const name = (opt.dataset.option1 || '').trim();
    const cssName = name.toLowerCase().replace(/\s+/g, '');

    dot.style.backgroundImage = '';
    dot.style.backgroundColor = '';

    if (img) {
      dot.style.backgroundImage = `url("${img}")`;
      dot.hidden = false;
    } else if (hex) {
      dot.style.backgroundColor = hex;
      dot.hidden = false;
    } else if (cssColorIsValid(name) || cssColorIsValid(cssName)) {
      dot.style.backgroundColor = cssColorIsValid(name) ? name : cssName;
      dot.hidden = false;
    } else {
      dot.hidden = true;
    }
  };

  // ---- qty stepper helpers ----
  // Current stepper value, clamped to an integer >= 1. Cards without
  // the stepper (metafield off) always report 1.
  const getQty = (root) => {
    const input = root.querySelector('[data-cart-upsell-qty-input]');
    if (!input) return 1;
    const n = Math.floor(Number(input.value));
    return Number.isFinite(n) && n >= 1 ? n : 1;
  };

  // Format cents in the card's currency. Locale pinned to en-US so the
  // symbol carries the locale-disambiguating prefix (A$, CA$, ...) -
  // same approach as the AOV bundler tweaks snippet. Falls back to the
  // exact server-rendered qty-1 string on any surprise.
  const formatCents = (cents, currency, fallback) => {
    const n = Number(cents);
    if (!Number.isFinite(n) || !currency) return fallback;
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n / 100);
    } catch (e) {
      return fallback;
    }
  };

  // Reflect the selected <option> and qty into the price / compare /
  // % off / image / dot. Pure DOM - no network. Single-variant products
  // render no <select>; unit prices then come from the card's dataset.
  const syncSelection = (root) => {
    const card = root;
    if (!card) return;
    const select = root.querySelector('[data-cart-upsell-variant]');
    const opt = select ? select.options[select.selectedIndex] : null;
    const unit = opt ? opt.dataset : card.dataset;
    const qty = getQty(root);
    const currency = card.dataset.currency;

    const priceEl = root.querySelector('[data-cart-upsell-price]');
    const compareEl = root.querySelector('[data-cart-upsell-compare]');
    const percentEl = root.querySelector('[data-cart-upsell-percent]');
    const mediaImg = root.querySelector('[data-cart-upsell-media] img');

    // Prefer exact market-aware server strings where we have them: the
    // qty-1 strings, or the default-qty strings (match_qty case - only
    // valid on the server-selected variant, hence defaultSelected).
    // Anything else: multiply raw cents and format client-side.
    const defaultQty = Math.floor(Number(card.dataset.defaultQty)) || 1;
    const atDefault = qty === defaultQty && (!opt || opt.defaultSelected);
    const unitShownStr = unit.deal || unit.price;
    const unitShownCents = unit.dealCents || unit.priceCents;
    if (priceEl) {
      if (qty === 1) {
        priceEl.textContent = unitShownStr;
      } else if (atDefault && card.dataset.defaultPrice) {
        priceEl.textContent = card.dataset.defaultPrice;
      } else {
        priceEl.textContent = formatCents(Number(unitShownCents) * qty, currency, unitShownStr);
      }
    }

    if (compareEl) {
      if (unit.compare) {
        if (qty === 1) {
          compareEl.textContent = unit.compare;
        } else if (atDefault && card.dataset.defaultCompare) {
          compareEl.textContent = card.dataset.defaultCompare;
        } else {
          compareEl.textContent = formatCents(Number(unit.compareCents || unit.priceCents) * qty, currency, unit.compare);
        }
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (percentEl) {
      if (unit.percent) {
        percentEl.textContent = `${unit.percent}% off`;
        percentEl.hidden = false;
      } else {
        percentEl.hidden = true;
      }
    }
    if (mediaImg && opt && opt.dataset.image) {
      // Drop any srcset so the browser uses the src we set, not the
      // original variant's responsive set.
      mediaImg.removeAttribute('srcset');
      mediaImg.src = opt.dataset.image;
    }

    if (opt) updateDot(root, opt);

    // Point the "view details" modal triggers at the selected variant, so
    // the modal's gallery opens on that variant's image (the modal-view
    // template has display_variant_image_first enabled). Rebuilt from the
    // clean base each time so the &variant param never stacks.
    if (opt && opt.dataset.id) {
      root.querySelectorAll('[data-cart-upsell-modal-trigger]').forEach((btn) => {
        const base = (btn.getAttribute('data-product-url') || '').split('&variant=')[0];
        btn.setAttribute('data-product-url', base + '&variant=' + opt.dataset.id);
      });
    }
  };

  const setBusy = (root, busy) => {
    const card = root;
    if (card) card.classList.toggle('is-busy', busy);
  };

  // ---- delegated handlers (survive innerHTML swaps) ----

  // Variant change: update the visible price/image/dot. If the item is
  // already in the cart, swap the cart line to the newly chosen variant.
  // Variant dropdown: just preview the selected colour (price + image).
  // Under Option B the card only shows BEFORE adding, so there's no
  // in-cart line to swap here - the customer picks, then taps "+".
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-cart-upsell-variant]');
    if (select) {
      syncSelection(select.closest('[data-cart-upsell-card]'));
      return;
    }
    // Typed qty: clamp to an integer >= 1 and re-sync the prices.
    const qtyInput = event.target.closest('[data-cart-upsell-qty-input]');
    if (qtyInput) {
      const root = qtyInput.closest('[data-cart-upsell-card]');
      qtyInput.value = getQty(root);
      syncSelection(root);
    }
  });

  // Stepper - / + : pure UI (no cart call until "+ add"). Min 1, no max.
  document.addEventListener('click', (event) => {
    const minus = event.target.closest('[data-cart-upsell-qty-minus]');
    const plus = event.target.closest('[data-cart-upsell-qty-plus]');
    if (!minus && !plus) return;
    const root = (minus || plus).closest('[data-cart-upsell-card]');
    const input = root.querySelector('[data-cart-upsell-qty-input]');
    if (!input) return;
    const next = getQty(root) + (plus ? 1 : -1);
    input.value = Math.max(1, next);
    syncSelection(root);
  });

  // "+" button: add the selected variant. On success the card hides on
  // the section refresh (the upsell is now a normal cart line), so we
  // don't need to reset the busy/disabled state.
  document.addEventListener('click', async (event) => {
    const addBtn = event.target.closest('[data-cart-upsell-add]');
    if (!addBtn || addBtn.disabled) return;

    const card = addBtn.closest('[data-cart-upsell-card]');
    const root = card;
    const error = card.closest('[data-cart-upsell-root]').querySelector('[data-cart-upsell-error]');
    error.hidden = true;
    const select = card.querySelector('[data-cart-upsell-variant]');
    const opt = select ? select.options[select.selectedIndex] : null;
    const variantId = opt ? Number(opt.dataset.id) : Number(card.dataset.variantId);

    if (!variantId) {
      console.error('[Upsell] no variant id to add');
      return;
    }

    setBusy(root, true);
    addBtn.disabled = true;
    try {
      await postCartChange(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { items: [{ id: variantId, quantity: getQty(root) }] });
      document.querySelector('[data-cart-upsell-card]:not([hidden]) [data-cart-upsell-add]')?.focus();
    } catch (e) {
      console.error('[Upsell] add error', e);
      setBusy(root, false);
      addBtn.disabled = false;
      error.textContent = e.message;
      error.hidden = false;
    }
  });

  // ---- observe external cart changes ----
  // When the cart changes outside our control (theme remove/qty, the
  // bundle app, etc.) re-render our row from the server so it
  // appears / hides / updates correctly. We don't modify the in-flight
  // request, so we never fight other cart apps.
  let refreshing = false;
  let refreshAgain = false;
  const refreshCardOnly = async () => {
    if (refreshing) { refreshAgain = true; return; }
    refreshing = true;
    const requestRevision = revision;
    try {
      const res = await fetch(`${location.pathname}?sections=cart-drawer`, { credentials: 'same-origin' });
      if (res.ok) {
        const data = await res.json();
        if (requestRevision === revision) applyDrawerSections(data);
        else refreshAgain = true;
      }
    } catch (e) {
      console.error('[Upsell] refresh error', e);
    } finally {
      refreshing = false;
      if (refreshAgain) { refreshAgain = false; refreshCardOnly(); }
    }
  };

  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || String(args[0]);
      if (
        typeof url === 'string' &&
        !url.includes(OWN_MARKER) &&
        /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url) &&
        response.ok
      ) {
        revision++;
        Promise.resolve().then(refreshCardOnly);
      }
    } catch (_) { /* never break the underlying fetch */ }
    return response;
  };

  // Move any server-rendered modal out of the drawer on load, and sync
  // the initial selection (image/price) for any visible card.
  function initialize(root, preferredHandle) {
    const cards = [...root.querySelectorAll('[data-cart-upsell-card]')];
    cards.forEach(card => syncSelection(card));
    showOffer(root, Math.max(0, cards.findIndex(card => card.dataset.upsellHandle === preferredHandle)));
  }
  relocateModals();
  document.querySelectorAll('[data-cart-upsell-root]').forEach(root => initialize(root));
  const drawer = document.getElementById('CartDrawer');
  if (drawer) new MutationObserver(() => {
    drawer.querySelectorAll('[data-cart-upsell-root]').forEach(root => {
      if (root.querySelector('[data-cart-upsell-carousel]:not([data-initialized])')) initialize(root);
    });
    relocateModals();
  }).observe(drawer, { childList: true, subtree: true });
})();
